/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./config.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import {
  applyIpFlagUpdate,
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { EXCHANGES_API_ENDPOINTS as cfg } from './config';
import type { ListPublicExchangesResponse } from './types';
import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query';

const tags = [
  'Exchanges',
  'ExchangeChangelogs',
  'ExchangeSyncLogs',
  'PublicExchanges',
  'ExchangeIps',
  'ExchangeRegions',
] as const;
type TagTypes = (typeof tags)[number];

// The caller-scoped `listPublicExchanges` response has no schema in the OpenAPI
// spec, so codegen types it as `Blob`. Re-type it to `PublicExchange[]` — the
// endpoint is otherwise fully generated (see `config.ts`).
type Definitions = DefinitionsFromApi<typeof generatedApi>;
type UpdatedDefinitions = Omit<Definitions, typeof cfg.listPublicExchanges.generated> & {
  [cfg.listPublicExchanges.generated]: OverrideResultType<
    Definitions[typeof cfg.listPublicExchanges.generated],
    ListPublicExchangesResponse
  >;
};

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `Exchanges` tag type is used for both the collection and individual
 * entities. The list query provides a per-item tag for every exchange plus a
 * dedicated `{ type: 'Exchanges', id: 'LIST' }` tag, so that:
 * - updating/deleting a single exchange invalidates only that exchange's
 *   `{ id }` tag, which refetches both the affected `getExchange` query and the
 *   list (the list provides that same per-item tag);
 * - creating an exchange invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated entities.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` workaround in
 * `onQueryStarted`: that was attributed to redux-toolkit#5093, but that issue is
 * about the `isSuccess` flag during pessimistic updates, not tag invalidation —
 * `invalidatesTags` propagates to the list correctly via the per-item tags.
 *
 * An exchange's own IP addresses and its region links (SKY-4604) are separate
 * collections per exchange, cached under `{ type: 'ExchangeIps', id: exchangeId }`
 * and `{ type: 'ExchangeRegions', id: exchangeId }`. Their mutations refetch
 * that collection plus the exchanges `LIST`, whose `ipsMM`/`ipsES` the backend
 * resolves from them. They deliberately leave the exchange's own `{ id }` tag
 * alone: the details page shows neither field, and refetching the exchange
 * would re-seed the details form's initial values and discard any other field
 * the admin is editing. An IP update also patches the cached IP list
 * optimistically with the flags it sends (undone if it fails), so a flag
 * switch follows the click at once instead of after the refetch.
 */
export const exchangesApi = generatedApi.enhanceEndpoints<TagTypes, UpdatedDefinitions>({
  addTagTypes: tags,
  endpoints: {
    [cfg.createExchange.generated]: {
      invalidatesTags: [{ type: 'Exchanges', id: LIST_TAG_ID }],
    },
    [cfg.listExchanges.generated]: {
      providesTags: (result) => provideListTags(result, 'Exchanges'),
    },
    [cfg.getExchange.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Exchanges', id: arg.id }],
    },
    // Changelogs use a dedicated tag, deliberately kept off the `Exchanges` tag:
    // a sync must NOT auto-refetch an open changelogs subscription at POST time,
    // because the backend populates the data asynchronously. The tag is instead
    // invalidated by the WS `sync-finished` event (ws-assistant-effects.ts),
    // which fires once the backend has actually written the data.
    [cfg.getExchangeChangelogs.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'ExchangeChangelogs', id: arg.id }],
    },
    // Sync logs get their own dedicated tag, kept off the `Exchanges` tag for
    // the same reason as changelogs. Per sync cycle the WS channel refreshes
    // them twice: `sync-started` (meant to surface the new RUNNING row — note
    // the backend currently publishes it before committing that row, so this
    // pass often still sees the old list) and `sync-finished` (the completed
    // verdict). See ws-assistant-effects.ts.
    [cfg.getExchangeSyncLogs.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'ExchangeSyncLogs', id: arg.id }],
    },
    [cfg.updateExchange.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Exchanges', id: arg.id }],
    },
    [cfg.deleteExchange.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Exchanges', id: arg.id }],
    },
    // The sync POST only queues the sync, so it invalidates nothing that the
    // backend hasn't written yet: the `Exchanges` tag covers the immediate row
    // update, while changelogs/sync-logs are invalidated later by the WS
    // `sync-started`/`sync-finished` events — see the notes above.
    [cfg.syncExchange.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Exchanges', id: arg.id }],
    },
    // Caller-scoped exchange list, cached per `scope` (mm/es) so the MM and ES
    // lists never overwrite each other. Kept on a dedicated `PublicExchanges`
    // tag so admin CRUD mutations don't invalidate it.
    [cfg.listPublicExchanges.generated]: {
      providesTags: (result) => provideListTags(result, 'PublicExchanges'),
    },
    [cfg.listExchangeIps.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'ExchangeIps', id: arg.id }],
    },
    [cfg.createExchangeIp.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExchangeIps', id: arg.id },
        { type: 'Exchanges', id: LIST_TAG_ID },
      ],
    },
    [cfg.updateExchangeIp.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          generatedApi.util.updateQueryData(
            cfg.listExchangeIps.generated,
            { id: arg.id },
            (ips) => {
              const ip = ips.find(({ id }) => id === arg.ipId);
              if (ip) {
                applyIpFlagUpdate(ip, arg.updateExchangeIpBody);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          // The caller reports the failure (it unwraps the mutation); here
          // the list only goes back to what is stored.
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExchangeIps', id: arg.id },
        { type: 'Exchanges', id: LIST_TAG_ID },
      ],
    },
    [cfg.deleteExchangeIp.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExchangeIps', id: arg.id },
        { type: 'Exchanges', id: LIST_TAG_ID },
      ],
    },
    [cfg.listExchangeRegions.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'ExchangeRegions', id: arg.id }],
    },
    [cfg.createExchangeRegion.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExchangeRegions', id: arg.id },
        { type: 'Exchanges', id: LIST_TAG_ID },
      ],
    },
    [cfg.deleteExchangeRegion.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExchangeRegions', id: arg.id },
        { type: 'Exchanges', id: LIST_TAG_ID },
      ],
    },
  },
});

// Re-export hooks with cleaner names via endpoint definitions
export const hookExports: HookExports<typeof cfg, typeof exchangesApi> = createHookExports(
  cfg,
  exchangesApi,
);

// Re-export endpoints object with cleaner names for direct usage
export const exchangesApiEndpoints = createEndpointExports(cfg, exchangesApi);
